<template>
  <div v-if="profilesVisible" id="vpn-profile__table-container">
    <v-flex d-flex>
      <v-subheader class="font-weight-bold">
        <span id="vpn-profile__rows">
          <span id="vpn-profile__total-string">Total: </span>
          <span id="vpn-profile__rows-count">{{ rowsCount }}</span>
        </span>
      </v-subheader>
      <v-spacer></v-spacer>
      <v-text-field
        v-model="search"
        :label="$t('actions.search')"
        class="mx-2"
        prepend-icon="mdi-magnify"
        single-line
        clearable
        id="vpn-profile__table-search"
      ></v-text-field>
    </v-flex>
    <v-data-table
      ref="dataContainer"
      :headers="headers"
      :items="loadItems"
      class="elevation-1"
      :loading="loading"
      hide-default-footer
      :search="search"
      style="min-height: 100%;"
      id="vpn-profile__table"
      must-sort
      :items-per-page="loadItems.length"
      fixed-header
      :height="loadItems.length > 5 ? '300px' : '100%'"
    >
      <template v-slot:[`item.is_managed`]="props">
        <v-simple-checkbox
          readonly
          disabled
          color="primary"
          hide-details
          v-model="props.item.is_managed"
          :id="'vpn-profile__table-is_managed-' + props.index"
          class="align-center justify-center"
        ></v-simple-checkbox>
      </template>
      <template v-slot:[`item.general_config.encryption_type`]="{ item }">
        <span v-for="(type, i) in encryption_types" :key="i">{{
          item.general_config.encryption_type === type.value ? type.text : ""
        }}</span>
      </template>
      <template v-slot:[`item.general_config.compression_type`]="{ item }">
        <span v-for="(type, i) in compression_types" :key="i">{{
          item.general_config.compression_type === type.value ? type.text : ""
        }}</span>
      </template>
      <template v-slot:[`item.general_config.layer_type`]="{ item }">
        <span v-for="(type, i) in layer_types" :key="i">{{
          item.general_config.layer_type === type.value ? type.text : ""
        }}</span>
      </template>
      <template v-slot:[`item.is_custom`]="props">
        <v-simple-checkbox
          readonly
          disabled
          color="primary"
          hide-details
          v-model="props.item.is_custom"
          class="align-center justify-center"
          :id="'vpn-profile__table-is_custom-' + props.index"
        >
        </v-simple-checkbox>
      </template>
      <template v-slot:[`item.is_private`]="props">
        <v-simple-checkbox
          readonly
          disabled
          color="primary"
          hide-details
          v-model="props.item.is_private"
          class="align-center justify-center"
          :id="'vpn-profile__table-is_private-' + props.index"
        >
        </v-simple-checkbox>
      </template>
      <template v-slot:[`item.is_enabled`]="props">
        <v-simple-checkbox
          readonly
          disabled
          color="primary"
          hide-details
          v-model="props.item.is_enabled"
          class="align-center justify-center"
          :id="'vpn-profile__table-is_enabled-' + props.index"
        ></v-simple-checkbox>
      </template>
      <template v-slot:[`item.action`]="props">
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              class="mr-6"
              @click="editItem(props.item)"
              :id="'vpn-profile__edit-' + props.index"
              :disabled="!isPermitted(PermissionEnum.OPENVPN_PROFILE.UPDATE)"
              v-on="on"
              v-bind="attrs"
              >mdi-pencil</v-icon
            >
          </template>
          <span>{{ $t("actions.edit") }}</span>
        </v-tooltip>
        <v-tooltip bottom>
          <template v-slot:activator="{ on, attrs }">
            <v-icon
              small
              @click="deleteItem(props.item)"
              :id="'vpn-profile__delete-' + props.index"
              :disabled="!isPermitted(PermissionEnum.OPENVPN_PROFILE.DELETE)"
              v-on="on"
              v-bind="attrs"
              >mdi-delete</v-icon
            >
          </template>
          <span>{{ $t("actions.delete") }}</span>
        </v-tooltip>
      </template>
    </v-data-table>
  </div>
</template>

<script>
import { handleError } from "@/utils/error";
import { permissionMixin } from "@/mixins/permissions";

export default {
  mixins: [permissionMixin],
  data() {
    return {
      headers: [
        {
          text: this.$t("modules.dataPath.vpn.vpnProfileName"),
          align: "left",
          value: "name"
        },
        {
          text: this.$t("modules.dataPath.vpn.managed"),
          align: "left",
          value: "is_managed"
        },
        {
          text: this.$t("modules.dataPath.vpn.encryption"),
          align: "left",
          value: "general_config.encryption_type"
        },
        {
          text: this.$t("modules.dataPath.vpn.compression"),
          align: "left",
          value: "general_config.compression_type"
        },
        {
          text: this.$t("modules.dataPath.vpn.layer"),
          align: "left",
          value: "general_config.layer_type"
        },
        {
          text: this.$t("modules.dataPath.custom"),
          align: "left",
          value: "is_custom"
        },
        {
          text: this.$t("modules.dataPath.vpn.private"),
          align: "left",
          value: "is_private"
        },
        {
          text: this.$t("modules.dataPath.vpn.serverIpAddress"),
          align: "left",
          value: "server_config.server_address"
        },
        {
          text: this.$t("modules.dataPath.vpn.port"),
          align: "left",
          value: "server_config.port"
        },
        {
          text: this.$t("modules.dataPath.enabled"),
          align: "center",
          value: "is_enabled"
        },
        {
          text: this.$t("actions.actions"),
          align: "center",
          value: "action",
          sortable: false
        }
      ],
      loadItems: [],
      loading: false,
      encryption_types: [
        {
          text: this.$t("modules.dataPath.vpn.none"),
          value: 1
        },
        {
          text: this.$t("modules.dataPath.vpn.blowfish"),
          value: 2
        },
        {
          text: this.$t("modules.dataPath.vpn.aes256"),
          value: 3
        }
      ],
      compression_types: [
        {
          text: this.$t("modules.dataPath.vpn.none"),
          value: 1
        },
        {
          text: this.$t("modules.dataPath.vpn.lzo"),
          value: 2
        },
        {
          text: this.$t("modules.dataPath.vpn.lz4"),
          value: 3
        }
      ],
      layer_types: [
        {
          text: this.$t("modules.dataPath.vpn.layer2"),
          value: 1
        },
        {
          text: this.$t("modules.dataPath.vpn.layer3"),
          value: 2
        }
      ]
    };
  },
  computed: {
    profilesVisible: {
      get() {
        return this.$store.state.dataPath.vpnProfile.profilesVisible;
      }
    },
    vpnProfiles() {
      return this.$store.state.dataPath.vpnProfile.vpnProfiles.map(item => {
        if (item.advanced_config) {
          item.is_custom = true;
        } else {
          item.is_custom = false;
        }
        return item;
      });
    },
    rowsCount() {
      return this.loadItems.length;
    },
    search: {
      get() {
        return this.$store.state.dataPath.vpnProfile.search || "";
      },
      set(val) {
        this.$store.commit("dataPath/updateVpnProfileSearch", val);
      }
    },
    vpnProfileDialog: {
      get() {
        return this.$store.state.dataPath.vpnProfile.vpnProfileDialog;
      },
      set(val) {
        this.$store.dispatch("dataPath/toggleVpnProfileDialog", val);
      }
    }
  },
  created() {
    this.initialize();
  },
  methods: {
    initialize() {
      this.loading = true;
      this.$store
        .dispatch("dataPath/fetchVpnProfile")
        .then(() => {
          if (this.search) {
            this.searchItems(this.search);
          } else {
            this.loadItems = this.vpnProfiles;
          }
        })
        .catch(error => {
          handleError(error);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    searchItems(val) {
      this.loadItems = this.vpnProfiles.filter(o =>
        this.headers
          .filter(item => item.value !== "action")
          .map(item => item.value)
          .some(k => {
            if (o[k]) {
              return (
                o[k]
                  .toString()
                  .toLowerCase()
                  .indexOf(val.toLowerCase()) > -1
              );
            }
          })
      );
    },
    editItem(item) {
      let editedIndex = this.vpnProfiles.indexOf(item);
      this.$store.commit("dataPath/updateVpnProfileFormTitle", 2);
      this.$store.commit("dataPath/updateVpnProfileIndex", editedIndex);
      this.$store.dispatch("dataPath/editVpnProfile", item);
      this.vpnProfileDialog = true;
    },
    deleteItem(item) {
      this.loading = true;
      let confirmed = confirm(this.$t("modules.dataPath.vpn.confirmDelete"));
      if (confirmed) {
        this.$store
          .dispatch("dataPath/deleteVpnProfile", item.id)
          .catch(error => {
            handleError(error);
          })
          .finally(() => {
            this.loading = false;
          });
      } else {
        this.loading = false;
      }
    }
  },
  watch: {
    search(val) {
      this.searchItems(val);
    },
    vpnProfiles() {
      if (!this.search) {
        this.loadItems = this.vpnProfiles;
      } else {
        this.searchItems(this.search);
      }
    }
  },
  beforeDestroy() {
    this.search = "";
  }
};
</script>
